// src/pages/OwnerPortal.js
// View for non-admin system owners — shows their systems with user management.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sharedCredentialsApi as api } from "../utils/api";
import { loadConfig } from "../utils/config";
import { auditStatus, formatDue, formatDate } from "../utils/dates";
import PeoplePicker from "../components/PeoplePicker";
import { SystemImageDisplay } from "../components/SystemImage";

const STATUS_BADGE = { ok: "badge-ok", "due-soon": "badge-due", overdue: "badge-overdue", unknown: "badge-neutral" };
const STATUS_LABEL = { ok: "Up to date", "due-soon": "Due soon", overdue: "Overdue", unknown: "Not set" };

const FALLBACK_LEVELS = ["Admin", "Editor", "Viewer", "Read-Only"];

// Inline add-user row shown at bottom of user table
function AddUserRow({ systemId, accessLevels, onAdded }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [notes,   setNotes]   = useState("");
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [open,    setOpen]    = useState(false);

  const toggle = (level) => setSelectedLevels(prev =>
    prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]
  );

  const handleAdd = async () => {
    if (!selectedUser) { setError("Select a user first"); return; }
    if (selectedLevels.length === 0) { setError("Select at least one role"); return; }
    setSaving(true); setError("");
    try {
      const accessLevel = selectedLevels.length === 1 ? selectedLevels[0] : selectedLevels;
      await api.addUser(systemId, {
        upn: selectedUser.upn, displayName: selectedUser.displayName,
        email: selectedUser.email, accessLevel, notes, active: true,
      });
      setSelectedUser(null); setSelectedLevels([]); setNotes(""); setOpen(false);
      onAdded();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <tr>
        <td colSpan={4} style={{ padding: "8px 12px" }}>
          <button className="btn-ghost" style={{ fontSize: 13, color: "var(--purple)" }}
            onClick={() => setOpen(true)}>+ Add User</button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ background: "var(--purple-light)" }}>
      <td colSpan={4} style={{ padding: "10px 12px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-600)", marginBottom: 4 }}>Person</div>
              <PeoplePicker value={selectedUser} onChange={setSelectedUser} placeholder="Search for a person…" />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-600)", marginBottom: 4 }}>Notes (optional)</div>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Any notes about this access…" style={{ fontSize: 13 }} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-600)", marginBottom: 4 }}>Permission(s)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {accessLevels.map(level => {
                const active = selectedLevels.includes(level);
                return (
                  <button key={level} type="button" onClick={() => toggle(level)} style={{
                    padding: "4px 10px", borderRadius: 99, fontSize: 12, cursor: "pointer",
                    background: active ? "var(--purple)" : "var(--gray-100)",
                    color: active ? "#fff" : "var(--gray-600)", fontWeight: active ? 600 : 400,
                  }}>
                    {active && "✓ "}{level}
                  </button>
                );
              })}
            </div>
          </div>
          {error && <div style={{ fontSize: 12, color: "var(--red)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn-primary" style={{ fontSize: 12, padding: "5px 14px" }}
              onClick={handleAdd} disabled={saving}>
              {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : "Add"}
            </button>
            <button className="btn-ghost" style={{ fontSize: 12 }}
              onClick={() => { setOpen(false); setError(""); setSelectedUser(null); setSelectedLevels([]); }}>
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// Expanded system card with user table
function SystemCard({ sys, accessLevels, onReload }) {
  const navigate  = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [users,    setUsers]    = useState([]);
  const [uLoading, setULoading] = useState(false);
  const [removing, setRemoving] = useState(null);
  const status = auditStatus(sys.nextAuditDue);

  const loadUsers = useCallback(async () => {
    if (!expanded) return;
    setULoading(true);
    try {
      const res  = await fetch(`/api/shared-credentials/${sys.id}`);
      const data = await res.json();
      setUsers((data.users || []).filter(u => u.active));
    } catch { setUsers([]); }
    finally { setULoading(false); }
  }, [sys.id, expanded]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleRemove = async (u) => {
    if (!window.confirm(`Remove ${u.displayName} from ${sys.name}?`)) return;
    setRemoving(u.id);
    try {
      await api.removeUser(sys.id, u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      onReload();
    } catch (err) { alert("Remove failed: " + err.message); }
    finally { setRemoving(null); }
  };

  const formatAccess = (level) => {
    if (!level) return "—";
    if (Array.isArray(level)) return level.join(", ");
    return level;
  };

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "14px 18px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <h3 style={{ fontSize: 15, fontWeight: 600 }}>{sys.name}</h3>
            {sys.category   && <span className="badge badge-info"    style={{ fontSize: 11 }}>{sys.category}</span>}
            {sys.systemType && <span className="badge badge-neutral" style={{ fontSize: 11 }}>{sys.systemType}</span>}
            <span className={`badge ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>
          </div>
          {sys.description && <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 6 }}>{sys.description}</p>}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "var(--gray-500)" }}>
            {sys.location && <span>📍 {sys.location}</span>}
            <span>📅 Next review: <strong style={{ color: status === "overdue" ? "var(--red)" : "inherit" }}>{formatDue(sys.nextAuditDue)}</strong></span>
            {sys.lastAuditDate && <span>🕐 Last reviewed: {formatDate(sys.lastAuditDate)}</span>}
            <span>👥 {sys.userCount || 0} user{sys.userCount !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button className="btn-ghost" style={{ fontSize: 13 }}
            onClick={() => setExpanded(e => !e)}>
            {expanded ? "▲ Hide Users" : "▼ Manage Users"}
          </button>
          <button className={status === "overdue" ? "btn-primary" : "btn-secondary"}
            onClick={() => navigate(`/shared-credentials/${sys.id}/review`)}>
            {status === "overdue" ? "⚠ Review Now" : "Start Review"}
          </button>
        </div>
      </div>

      {/* Users panel */}
      {expanded && (
        <div>
          {uLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><span className="spinner" /></div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--gray-50)" }}>
                  {["Name", "Permission", "Notes", ""].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: "14px 12px", fontSize: 13, color: "var(--gray-400)", fontStyle: "italic" }}>No users assigned yet.</td></tr>
                )}
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{u.displayName}</div>
                      <div style={{ fontSize: 11, color: "var(--gray-400)" }}>{u.upn}</div>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      {(Array.isArray(u.accessLevel) ? u.accessLevel : [u.accessLevel]).filter(Boolean).map(l => (
                        <span key={l} className="badge badge-info" style={{ fontSize: 11, marginRight: 3 }}>{l}</span>
                      ))}
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--gray-500)" }}>{u.notes || "—"}</td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>
                      <button className="btn-ghost" style={{ fontSize: 12, color: "var(--red)" }}
                        disabled={removing === u.id}
                        onClick={() => handleRemove(u)}>
                        {removing === u.id ? <span className="spinner" style={{ width: 12, height: 12 }} /> : "Remove"}
                      </button>
                    </td>
                  </tr>
                ))}
                <AddUserRow systemId={sys.id} accessLevels={accessLevels} onAdded={() => { loadUsers(); onReload(); }} />
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function OwnerPortal({ user }) {
  const navigate          = useNavigate();
  const [systems,         setSystems]         = useState([]);
  const [view,            setView]            = useState(() => localStorage.getItem("sharedcreds_mysys_view") || "card");
  const [assignedSystems, setAssignedSystems] = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [accessLevels,    setAccessLevels]    = useState(FALLBACK_LEVELS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all     = await api.list();
      const upn     = user?.upn?.toLowerCase();
      const owned   = all.filter(s => (s.accessOwners || []).some(o => o.upn?.toLowerCase() === upn));

      // For assigned: fetch users for each non-owned system and check if this user is listed
      const assignedResults = [];
      await Promise.allSettled(
        all.map(async sys => {
          try {
            const res   = await fetch(`/api/shared-credentials/${sys.id}`);
            const data  = await res.json();
            const match = (data.users || []).find(
              u => u.active && u.upn?.toLowerCase() === upn
            );
            if (match) {
              assignedResults.push({
                ...sys,
                myAccessLevel: Array.isArray(match.accessLevel)
                  ? match.accessLevel.join(", ")
                  : match.accessLevel || "",
              });
            }
          } catch {}
        })
      );

      setSystems(owned);
      setAssignedSystems(assignedResults.sort((a,b) => a.name.localeCompare(b.name)));
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    loadConfig().then(cfg => {
      if (cfg.defaultAccessLevels?.length) setAccessLevels(cfg.defaultAccessLevels);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">My Shared Credentials</div>
          <div className="page-subtitle">
            {loading ? "Loading…" : `${systems.length} owned · ${assignedSystems.length} assigned`}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign:"center", padding:48 }}><span className="spinner" /></div>
      ) : (
        <>
          {/* ── Shared Credentials I Own ── */}
          <div style={{ marginBottom:28 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <h2 style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--gray-500)", margin:0 }}>
                Shared Credentials I Own
              </h2>
              <div style={{ display:"flex", background:"var(--gray-100)", borderRadius:"var(--radius)", overflow:"hidden", padding:2 }}>
                <button onClick={() => { setView("list"); localStorage.setItem("sharedcreds_mysys_view","list"); }} style={{ padding:"5px 10px", border:"none", cursor:"pointer", background: view==="list" ? "var(--purple)" : "#fff", color: view==="list" ? "#fff" : "var(--gray-600)", fontSize:13 }} title="List view">☰</button>
                <button onClick={() => { setView("card"); localStorage.setItem("sharedcreds_mysys_view","card"); }} style={{ padding:"5px 10px", border:"none", cursor:"pointer", background: view==="card" ? "var(--purple)" : "#fff", color: view==="card" ? "#fff" : "var(--gray-600)", fontSize:13 }} title="Card view">⊞</button>
              </div>
            </div>
            {systems.length === 0 ? (
              <div className="card empty-state" style={{ padding:"20px" }}>
                <p>You are not listed as the owner of any registered shared credentials.</p>
              </div>
            ) : view === "list" ? (
              <div className="card" style={{ padding:0, overflow:"hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: 60 }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "16%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th></th>
                      <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 12, color: "var(--gray-500)" }}>Name</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 12, color: "var(--gray-500)" }}>Category</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 12, color: "var(--gray-500)" }}>Users</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 12, color: "var(--gray-500)" }}>Audit</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {systems.map(sys => {
                      const st = auditStatus(sys.nextAuditDue);
                      const statusColor = st==="ok" ? "var(--green)" : st==="due-soon" ? "var(--orange)" : "var(--red)";
                      return (
                        <tr key={sys.id} style={{ cursor:"pointer" }}
                          onClick={() => navigate(`/owner-shared-credential/${sys.id}`)}
                          onMouseEnter={e => e.currentTarget.style.background="var(--gray-50)"}
                          onMouseLeave={e => e.currentTarget.style.background=""}>
                          <td style={{ padding:"10px 8px 10px 14px" }}>
                            <SystemImageDisplay resource="shared-credentials" systemId={sys.id} systemName={sys.name} size={36} radius={6} cacheKey={sys.imageVersion} />
                          </td>
                          <td style={{ padding:"10px 12px" }}>
                            <div style={{ fontWeight:600, fontSize:13 }}>{sys.name}</div>
                            {sys.location && <div style={{ fontSize:11, color:"var(--gray-400)" }}>{sys.location}</div>}
                          </td>
                          <td style={{ padding:"10px 12px" }}><span className="badge badge-gray" style={{ fontSize:11 }}>{sys.category||"—"}</span></td>
                          <td style={{ padding:"10px 12px", fontSize:13, color:"var(--gray-500)" }}>{sys._userCount ?? "—"}</td>
                          <td style={{ padding:"10px 12px", fontSize:12, fontWeight:600, color:statusColor }}>{formatDue(sys.nextAuditDue)}</td>
                          <td style={{ padding:"10px 12px" }}><button className="btn-ghost" style={{ fontSize:12 }}>Manage →</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:12 }}>
                {systems.map(sys => {
                  const st = auditStatus(sys.nextAuditDue);
                  const statusColor = st==="ok" ? "var(--green)" : st==="due-soon" ? "var(--orange)" : "var(--red)";
                  return (
                    <div key={sys.id} className="card" style={{ padding:0, overflow:"hidden", cursor:"pointer", transition:"box-shadow .15s" }}
                      onClick={() => navigate(`/owner-shared-credential/${sys.id}`)}
                      onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,.12)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow=""}>
                      <div style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px" }}>
                        <SystemImageDisplay resource="shared-credentials" systemId={sys.id} systemName={sys.name} size={52} radius={8} cacheKey={sys.imageVersion} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:14, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{sys.name}</div>
                          {sys.category && <span className="badge badge-gray" style={{ fontSize:10 }}>{sys.category}</span>}
                        </div>
                      </div>
                      <div style={{ padding:"10px 16px" }}>
                        <div style={{ fontSize:11, fontWeight:600, color:statusColor }}>Audit due {formatDue(sys.nextAuditDue)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Shared Credentials I Am Assigned To ── */}
          <div style={{ marginBottom:28 }}>
            <h2 style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--gray-500)", marginBottom:10 }}>
              Shared Credentials I Am Assigned To
            </h2>
            {assignedSystems.length === 0 ? (
              <div className="card empty-state" style={{ padding:"20px" }}>
                <p>You have not been assigned access to any shared credentials.</p>
              </div>
            ) : view === "card" ? (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:12 }}>
                {assignedSystems.map(sys => (
                  <div key={sys.id} className="card" style={{ padding:0, overflow:"hidden" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px" }}>
                      <SystemImageDisplay resource="shared-credentials" systemId={sys.id} systemName={sys.name} size={48} radius={6} cacheKey={sys.imageVersion} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:13, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{sys.name}</div>
                        {sys.category && <span className="badge badge-gray" style={{ fontSize:10 }}>{sys.category}</span>}
                      </div>
                    </div>
                    <div style={{ padding:"10px 16px" }}>
                      <div style={{ fontSize:11, color:"var(--gray-500)", marginBottom:2 }}>My permission</div>
                      <span className="badge badge-purple" style={{ fontSize:11 }}>{sys.myAccessLevel || "—"}</span>
                      <div style={{ fontSize:11, color:"var(--gray-400)", marginTop:6 }}><span style={{ color:"var(--gray-500)" }}>System Owner:</span> {(sys.accessOwners||[]).map(o=>o.displayName).join(", ") || "No owner"}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card" style={{ padding:0, overflow:"hidden" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ background:"var(--gray-50)" }}>
                      <th style={{ width: 60 }}></th>
                      {["Name","Category","My Permission","Owner"].map(h => (
                        <th key={h} style={{ padding:"10px 16px", textAlign:"left", fontSize:11, fontWeight:600, color:"var(--gray-500)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {assignedSystems.map(sys => (
                      <tr key={sys.id}>
                        <td style={{ padding:"10px 8px 10px 14px" }}>
                          <SystemImageDisplay resource="shared-credentials" systemId={sys.id} systemName={sys.name} size={36} radius={6} cacheKey={sys.imageVersion} />
                        </td>
                        <td style={{ padding:"11px 16px" }}>
                          <div style={{ fontWeight:500, fontSize:13 }}>{sys.name}</div>
                          {sys.description && <div style={{ fontSize:11, color:"var(--gray-400)" }}>{sys.description}</div>}
                        </td>
                        <td style={{ padding:"11px 16px", fontSize:13, color:"var(--gray-600)" }}>
                          {sys.category ? <span className="badge badge-neutral" style={{ fontSize:11 }}>{sys.category}</span> : "—"}
                        </td>
                        <td style={{ padding:"11px 16px", fontSize:13 }}>
                          {sys.myAccessLevel
                            ? <span className="badge badge-info" style={{ fontSize:11 }}>{sys.myAccessLevel}</span>
                            : <span style={{ color:"var(--gray-400)" }}>—</span>}
                        </td>
                        <td style={{ padding:"11px 16px", fontSize:13, color:"var(--gray-600)" }}>
                          {(sys.accessOwners||[]).map(o=>o.displayName).join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
